/* Mobile link fix: keep touch targets mounted and ignore transient focus loss. */
if (!customElements.get('ffb-header')) {
  customElements.define('ffb-header', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.toggle = this.querySelector('.ffb-mobile-toggle');
      this.nav = this.querySelector('.ffb-nav');
      this.list = this.querySelector('.ffb-menu');
      if (!this.toggle || !this.nav || !this.list) return;
      this.items = [...this.list.children];
      this.moreItem = document.createElement('li');
      this.moreItem.className = 'ffb-menu-item ffb-more-item';
      this.moreItem.hidden = true;
      this.moreItem.innerHTML = '<details class="ffb-dropdown ffb-more"><summary class="ffb-top-link">More <span class="ffb-chevron ffb-more-chevron" aria-hidden="true">⌄</span></summary><div class="ffb-more-panel"><ul class="ffb-more-list" role="list"></ul></div></details>';
      this.list.append(this.moreItem);
      this.more = this.moreItem.querySelector('details');
      this.moreList = this.moreItem.querySelector('ul');
      this.menus = [...this.querySelectorAll('.ffb-dropdown')];
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.setAttribute('data-ready', '');
      this.updateMode = () => {
        this.toggle.hidden = this.desktop.matches;
        this.closeMenus();
        this.removeAttribute('data-menu-open');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.scheduleFit();
      };
      this.desktop.addEventListener('change', this.updateMode, options);
      this.updateMode();
      this.toggle.addEventListener('click', () => {
        const open = this.toggleAttribute('data-menu-open');
        this.toggle.setAttribute('aria-expanded', String(open));
        if (!open) this.closeMenus();
      }, options);
      this.menus.forEach(menu => {
        menu.addEventListener('pointerenter', event => {
          if (!this.desktop.matches || event.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimer);
          this.closeMenus(menu);
          menu.open = true;
        }, options);
        menu.addEventListener('pointerleave', event => {
          if (!this.desktop.matches || event.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => {
            if (!menu.contains(document.activeElement) && !menu.matches(':hover')) menu.open = false;
          }, 220);
        }, options);
        menu.addEventListener('toggle', () => {
          if (menu.open) this.closeMenus(menu);
        }, options);
      });
      this.addEventListener('focusout', event => {
        // Touch browsers may blur the summary without focusing the tapped link.
        // Keep the link visible until its native click/navigation completes.
        // An outside click is handled separately below.
        if (event.relatedTarget && !this.contains(event.relatedTarget)) {
          this.closeMenus();
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const focused = event.target.closest('.ffb-dropdown[open]');
        const open = focused || this.menus.find(menu => menu.open && menu !== this.more) || (this.more.open ? this.more : null);
        if (open) {
          open.open = false;
          open.querySelector('summary').focus();
        } else if (this.hasAttribute('data-menu-open')) {
          this.removeAttribute('data-menu-open');
          this.toggle.setAttribute('aria-expanded', 'false');
          this.toggle.focus();
        }
        event.stopPropagation();
      }, options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) {
          this.closeMenus();
          this.removeAttribute('data-menu-open');
          this.toggle.setAttribute('aria-expanded', 'false');
        }
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const menu = this.menus.find(item => item.dataset.blockId === event.detail.blockId);
        if (!menu) return;
        if (!this.desktop.matches) {
          this.setAttribute('data-menu-open', '');
          this.toggle.setAttribute('aria-expanded', 'true');
        }
        this.closeMenus(menu);
        if (this.more.contains(menu)) this.more.open = true;
        menu.open = true;
      }, options);
      document.addEventListener('shopify:block:deselect', () => this.closeMenus(), options);
      const observedWidths = new WeakMap();
      this.observer = new ResizeObserver(entries => {
        let widthChanged = false;
        for (const entry of entries) {
          const width = entry.contentRect.width;
          const previous = observedWidths.get(entry.target);
          if (previous === undefined || Math.abs(previous - width) > 0.5) {
            observedWidths.set(entry.target, width);
            widthChanged = true;
          }
        }
        // Opening a mobile dropdown changes height, not navigation capacity.
        if (widthChanged) this.scheduleFit();
      });
      this.observer.observe(this.nav);
      this.observer.observe(this.querySelector('.ffb-logo'));
      let viewportWidth = window.innerWidth;
      window.addEventListener('resize', () => {
        if (window.innerWidth === viewportWidth) return;
        viewportWidth = window.innerWidth;
        this.scheduleFit();
      }, options);
      document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleFit(); });
    }
    scheduleFit() {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(() => this.fitNavigation());
    }
    fitNavigation() {
      // Mobile has no overflow redistribution. Restore desktop overflow only
      // once when crossing the breakpoint; never remount mobile tap targets.
      if (!this.desktop.matches) {
        if (this.moreList.children.length) {
          this.items.forEach(item => this.list.insertBefore(item, this.moreItem));
        }
        this.moreItem.hidden = true;
        this.more.open = false;
        return;
      }
      const active = document.activeElement;
      const hadFocus = this.items.some(item => item.contains(active));
      this.items.forEach(item => this.list.insertBefore(item, this.moreItem));
      this.moreItem.hidden = true;
      if (this.desktop.matches) {
        const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
        const available = this.nav.clientWidth;
        const width = () => {
          const visible = [...this.list.children].filter(item => !item.hidden);
          return visible.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) + Math.max(0, visible.length - 1) * gap;
        };
        if (width() > available) {
          this.moreItem.hidden = false;
          for (let i = this.items.length - 1; i >= 0 && width() > available; i--) {
            this.moreList.prepend(this.items[i]);
          }
        }
      }
      if (this.moreItem.hidden) this.more.open = false;
      if (hadFocus) {
        if (this.more.contains(active)) this.more.open = true;
        active.focus({ preventScroll: true });
      }
    }
    closeMenus(except) {
      clearTimeout(this.closeTimer);
      this.menus.forEach(menu => {
        // Keep the More ancestor open while one of its child categories is open.
        if (menu !== except && !(except && (menu.contains(except) || except.contains(menu)))) menu.open = false;
      });
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.fitFrame);
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
      if (this.moreItem) {
        this.items.forEach(item => this.list.insertBefore(item, this.moreItem));
        this.moreItem.remove();
      }
    }
  });
}
